import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Modal,
  Radio,
  Tag,
  Tooltip,
} from 'antd';
import {
  debounce,
  filter,
  forEach,
  isEmpty,
  map,
  toInteger,
  uniq,
} from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Countdown from 'react-countdown';
import { useHistory, useLocation } from 'react-router-dom';
import MappedUnitsCell from '../../../../../components/MappedUnitsCell';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as CommentIcon } from '../../../../../assets/svg/comment-dots-solid.svg';
import { ReactComponent as FilterIcon } from '../../../../../assets/svg/sliders-solid.svg';
import ORDER_TAG_BULK_ACTIONS, {
  ADLOGGS_OPEN_LIBRARY_IDS,
  BREACH_TIME_CRITICAL_THRESHOLD,
  BREACH_TIME_WARNING_THRESHOLD,
  CANCELLATION_TYPE,
  CRITICAL_ORDERS_KEY,
  DATE_FORMATS,
  DELIVERY_PARTNER,
  DELIVERY_TYPE,
  DELIVERY_TYPE_OPTIONS,
  ORDER_TYPE_FILTER_OPTIONS,
  DISABLE_ORDER,
  DISABLE_ORDER_ITEM,
  EXPRESS_DELIVERY_EMOJI,
  EXPRESS_DELIVERY_TOOLTIP_TEXT,
  FULFILLMENT_STATUS,
  LIMIT,
  NEW_ORDER_EXPANDED_TABLE_ACTIONS_OPTIONS,
  NEW_ORDER_TABLE_ACTIONS_OPTIONS,
  ORDER_STATUS,
  ORDER_RIDER_REASSIGNABLE_STATUSES,
  ORDER_TAG_TYPES,
  ORDER_TYPE,
  ORDERS_POLLING_INTERVAL,
  QUICK_DELIVERY_EMOJI,
  QUICK_DELIVERY_TOOLTIP_TEXT,
  ROUTES,
  SORT,
  SORTING,
  STOCK_COLUMN_TOOLTIPS,
  TABLE_FILTER_KEYS,
  TRANSFER_ORDER_STATUS,
  USER_ROLES,
} from '../../../../../common/constants';
import {
  formatDate,
  getRowClassName,
  handleOpenUserDrawer,
  hasNonEmptyValueObj,
  hasPreOrderToy,
  renderer,
  shouldNotifyForNewOrders,
  toDayjsRange,
  humanizeEnum,
} from '../../../../../common/utils';
import ColumnTitleWithInfo from '../../../../../components/ColumnTitleWithInfo';
import CommonPreview from '../../../../../components/CommonPreview';
import CommonPreviewTable from '../../../../../components/CommonPreviewTable';
import CommonTable from '../../../../../components/CommonTable';
import FulfilmentPossibilityDrawer from '../../../../../components/FulfilmentPossibilityDrawer';
import LoaderComponent from '../../../../../components/LoaderComponent';
import BulkActionButton from '../../../../../components/BulkActionButton';
import OrderDynamicTags from '../../../../../components/OrderDynamicTags';
import OrderStatusSelect from '../../../../../components/OrderStatusSelect';
import SearchComponent from '../../../../../components/SearchComponent';
import SelectComponent from '../../../../../components/SelectComponent';
import ShowDelayedOrdersSwitch from '../../../../../components/ShowDelayedOrdersSwitch';
import ShowPreOrderSwitch from '../../../../../components/ShowPreOrderSwitch';
import SortDropdown from '../../../../../components/SortDropdown';
import { CITIES, PINCODES } from '../../../../../components/graphql/Queries';
import useInfiniteScroll from '../../../../../hooks/useInfiniteScroll';
import useOrderAlert from '../../../../../hooks/useOrderAlert';
import useRetainedTableState from '../../../../../hooks/useRetainedTableFilterState';
import AddCommentModal from '../../../../../libraryModules/orders/modules/all/components/AddCommentModal';
import AddDelayReasonModal from '../../../../../libraryModules/orders/modules/all/components/AddDelayReasonModal';
import UnitScanModal from '../../../../../components/UnitScan/UnitScanModal';
import { UNIT_SCAN_MODES } from '../../../../../components/UnitScan/constants';
import AllFilterOrderModal from '../../../../../libraryModules/orders/modules/all/components/AllFilterOrderModal';
import OrderTagsFilter from '../../../../../components/OrderTagsFilter';
import AllOrderItemModal from '../../../../../libraryModules/orders/modules/all/components/AllOrderItemModal';
import AllOrderList from '../../../../../libraryModules/orders/modules/all/components/AllOrderList';
import { ORDERS_SELECT } from '../../../../../libraryModules/orders/modules/all/graphql/Queries';
import { USERS } from '../../../../../libraryModules/raiseRequest/graphql/Queries';
import UserSelect from '../../../../payments/modules/endUser/components/UserSelect';
import ContentMasterModal from '../../all/components/ContentMasterModal';
import DelayReasonModal from '../../all/components/DelayReasonModal';
import NoteModal from '../../all/components/NoteModal';
import { CANCEL_ORDER_ADMIN } from '../../all/graphql/Mutations';
import {
  GET_READY_TO_SHIP_ORDERS,
  ORDER_ITEMS_ADMIN,
} from '../../all/graphql/Queries';
import {
  ASSIGN_DELIVERY_PARTNER_V2,
  UPDATE_ORDER_METADATA,
  UPDATE_ORDER_STATUS_ADMIN,
  UPDATE_ORDER_STATUS_ADMIN_V2,
} from '../graphql/Mutations';
import { LIBRARIES, NEW_ORDERS_ADMIN_V2 } from '../graphql/Queries';
import AssignDeliveryPartnerModal from './AssignDeliveryPartnerModal';
import BigToyIcon from './BigToyIcon';
import StatusFlag from './OrderTagRenderer';
import RenderCopyIcon from '../../../../../components/RenderCopyIcon';
import { isUnitsFeatureEnabledForLibrary } from '../../../../../hooks/useInventoryUnitsFeature';
import useToyDrawer from '../../../../../hooks/useToyDrawer';
import createOrderItemStaffColumns from '../../../../../components/OrderItemStaff/createOrderItemStaffColumns';
import { patchNestedOrderItemStaff } from '../../../../../components/OrderItemStaff/utils';
import BulkAssignStaffButton from '../../../../../components/OrderItemStaff/BulkAssignStaffButton';
import useNestedOrderItemSelection from '../../../../../components/OrderItemStaff/useNestedOrderItemSelection';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialNewDeliveryFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
  showPlacedOrderCount: true,
  showConfirmedOrderCount: true,
};

const initialNewDeliverySort = {
  field: 'createdAt',
  order: 'DESC',
};

const initialTableFilter = {
  skip: 0,
  limit: LIMIT,
  search: '',
};

const initialTableSort = {
  field: 'createdAt',
  order: 'DESC',
};

let filterScrollDebounceJob = null;

const sort = [
  {
    label: SORTING?.ORDER_ID_A_Z?.label,
    value: SORTING?.ORDER_ID_A_Z?.value,
    field: SORTING?.ORDER_ID_A_Z?.field,
    sort: SORTING?.ORDER_ID_A_Z?.sort,
  },
  {
    label: SORTING?.ORDER_ID_Z_A?.label,
    value: SORTING?.ORDER_ID_Z_A?.value,
    field: SORTING?.ORDER_ID_Z_A?.field,
    sort: SORTING?.ORDER_ID_Z_A?.sort,
  },
  {
    label: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.label,
    value: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.value,
    field: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.field,
    sort: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.sort,
  },
  {
    label: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.label,
    value: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.value,
    field: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.field,
    sort: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.sort,
  },
];

const NewOrderTable = ({
  exportFilter,
  setExportFilter,
  isAssignDelPartnerTab,
}) => {
  const location = useLocation();
  const [form] = Form.useForm();
  const [delayForm] = Form.useForm();
  const [assignDelPartnerForm] = Form.useForm();
  const {
    dispatch,
    state: {
      isDesktop,
      orderCount,
      commonPermissions: {
        isUserPermission,
        isToyPermission,
        isDeliveryPartnerPermission,
        isLibraryPermission,
      },
    },
  } = useContext(AppContext);
  const {
    hydrated,
    retainedState,
    saveRetainedFilterState,
  } = useRetainedTableState();

  const {
    playNotificationSound,
    playNotificationSoundForNewOrder,
  } = useOrderAlert();
  const history = useHistory();
  const { openToyDrawer } = useToyDrawer();
  const queryParams = new URLSearchParams(location?.search);
  const orderId = useRef(queryParams?.get('orderId') || '');
  const [orderList, setOrderList] = useState([]);
  const [possibilityDrawerVisible, setPossibilityDrawerVisible] = useState(
    false,
  );
  const [selectedPossibilityItem, setSelectedPossibilityItem] = useState(null);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const orderSearchTermRef = useRef(orderSearchTerm);
  const [isEmptyOrderList, setIsEmptyOrderList] = useState(false);
  const [order, setOrder] = useState({});
  const [orderItem, setOrderItem] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const sortedInfoRef = useRef(null);
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const paginationPropRef = useRef(initialPaginationValue);
  const [isOrderLoading, setIsOrderLoading] = useState(true);
  const [isNotePrompts, setIsNotePrompts] = useState(false);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isAddCommentLoading, setIsAddCommentLoading] = useState(false);
  const [isOrderAction, setIsOrderAction] = useState(false);
  const [isUnitScanOpen, setIsUnitScanOpen] = useState(false);
  const [isFulfillPrompts, setIsFulfillPrompts] = useState(false);
  const [isFulfillLoading, setIsFulfillLoading] = useState(false);
  const [isReadyLoading, setIsReadyLoading] = useState(false);
  const [isReadyPrompts, setIsReadyPrompts] = useState(false);
  const [isDelayReasonPrompts, setIsDelayReasonPrompts] = useState(false);
  const [isConfirmPrompts, setIsConfirmPrompts] = useState(false);
  const [isReasonPrompt, setIsReasonPrompt] = useState(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [nestedData, setNestedData] = useState({});
  const getStaffColumns = createOrderItemStaffColumns({
    canEdit: !isAssignDelPartnerTab,
    onReassigned: (...args) =>
      setNestedData((prev) =>
        patchNestedOrderItemStaff(prev, 'orderItemsAdmin', ...args),
      ),
  });
  const [isLoading, setIsLoading] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const finalWhereFiltersRef = useRef(null);
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const selectedTagIdsRef = useRef([]);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [visible, setVisible] = useState(false);
  const [toyImages, setToyImages] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [toyPreviewImage, setToyPreviewImage] = useState();
  const [isContentMasterModalOpen, setIsContentMasterModalOpen] = useState(
    false,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedOrdersById, setSelectedOrdersById] = useState({});

  const {
    selectedOrders: staffSelectedOrders,
    hasItemSelection,
    clearItemSelection,
    getNestedRowSelection,
    getSelectedOrderItems,
  } = useNestedOrderItemSelection();
  const [isDeliveryPartnerLoading, setIsDeliveryPartnerLoading] = useState(
    false,
  );
  const [isDeliveryPartnerPrompts, setIsDeliveryPartnerPrompts] = useState(
    false,
  );
  const [deliveryPartnerData, setDeliveryPartnerData] = useState({});
  const [existingDeliveryPartner, setExistingDeliveryPartnerData] = useState(
    {},
  );
  const existingDelPartnerRef = useRef(null);
  const [isDeliveryPartnerModalOpen, setDeliveryPartnerModalOpen] = useState(
    false,
  );
  const [isAllFilterModalOpen, setIsAllFilterModalOpen] = useState(false);
  const [isAllOrderItemModalOpen, setIsAllOrderItemModalOpen] = useState(false);
  const [isAllSortPopoverOpen, setIsAllSortPopoverOpen] = useState(false);
  const [isFetchMoreOrderLoading, setIsFetchMoreOrderLoading] = useState(false);
  const [thresholdData, setThresholdData] = useState({});

  const tempComment = useRef(null);
  const tempUnfulfilledReason = useRef(null);
  const [isActive, setIsActive] = useState(true);
  const isPollingRef = useRef(false);
  const previousCountRef = useRef(0);
  const isInitialMountRef = useRef(true);
  const statusFilter = isAssignDelPartnerTab
    ? ORDER_RIDER_REASSIGNABLE_STATUSES
    : [ORDER_STATUS?.PLACED?.value];
  const [initializedFromRetained, setInitializedFromRetained] = useState(false);

  useEffect(() => {
    if (!hydrated) return;
    if (initializedFromRetained) return;

    const retaineFiters =
      retainedState?.[
        isAssignDelPartnerTab
          ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
          : TABLE_FILTER_KEYS.NEW_ORDERS
      ];

    if (!retaineFiters) {
      setInitializedFromRetained(true);
      return;
    }
    const baseFilters = retaineFiters?.filters || {};
    const normalizedFilters = {
      ...baseFilters,
      createdAt: toDayjsRange(baseFilters?.createdAt),
      deliveredOn: toDayjsRange(baseFilters?.deliveredOn),
    };
    setFinalWhereFilters(normalizedFilters);
    finalWhereFiltersRef.current = normalizedFilters;
    setFilterTags(retaineFiters?.filterTags || {});
    setFinalFilterTags(retaineFiters?.filterTags || {});
    setSortedInfo(retaineFiters?.sort);
    sortedInfoRef.current = retaineFiters?.sort;

    setPaginationProp(retaineFiters?.paginationProp);
    paginationPropRef.current = retaineFiters?.paginationProp;

    setOrderSearchTerm(retaineFiters?.searchTerm);
    orderSearchTermRef.current = retaineFiters?.searchTerm;
    setInitializedFromRetained(true);
  }, [hydrated, initializedFromRetained, retainedState]);

  const [newOrdersAdminV2] = useLazyQuery(NEW_ORDERS_ADMIN_V2, {
    onCompleted: (response) => {
      const criticalThreshold = toInteger(
        response?.newOrdersAdminV2?.configs?.find(
          (config) => config?.key === BREACH_TIME_CRITICAL_THRESHOLD,
        )?.value,
      );
      const warningThreshold = toInteger(
        response?.newOrdersAdminV2?.configs?.find(
          (config) => config?.key === BREACH_TIME_WARNING_THRESHOLD,
        )?.value,
      );
      setThresholdData({
        criticalThreshold,
        warningThreshold,
      });
      if (isAssignDelPartnerTab) {
        dispatch({
          type: 'SET_ORDER_COUNT',
          data: {
            ...orderCount,
            confirmedOrderCount: response?.newOrdersAdminV2?.count,
            remainingConfirmedOrderCount:
              response?.newOrdersAdminV2?.confirmedOrderCount,
          },
        });
      } else {
        dispatch({
          type: 'SET_ORDER_COUNT',
          data: {
            ...orderCount,
            newOrderCount: response?.newOrdersAdminV2?.count,
            placedOrderCount: response?.newOrdersAdminV2?.placedOrderCount,
          },
        });
      }
      const newOrders = response?.newOrdersAdminV2?.data || [];
      const existingIds = new Set(
        orderList?.map((currentOrder) => currentOrder?.orderNumber),
      );
      const uniqueNewOrders = newOrders?.filter(
        (currentOrder) => !existingIds?.has(currentOrder.orderNumber),
      );

      const shouldNotify = shouldNotifyForNewOrders(
        uniqueNewOrders,
        orderList,
        isPollingRef,
        isInitialMountRef,
        finalWhereFilters,
        orderSearchTerm,
        previousCountRef,
      );
      if (shouldNotify && !isAssignDelPartnerTab) {
        playNotificationSoundForNewOrder(
          uniqueNewOrders,
          paginationProp?.current > 1,
        );
      }

      previousCountRef.current = response?.newOrdersAdminV2?.count;
      isInitialMountRef.current = false;
      if (isDesktop) {
        setOrderList([...response?.newOrdersAdminV2?.data]);
      } else {
        // eslint-disable-next-line no-lonely-if
        if (isPollingRef?.current) {
          setOrderList([...uniqueNewOrders, ...orderList]);
          isPollingRef.current = false;
        } else {
          setOrderList([...orderList, ...uniqueNewOrders]);
        }
      }
      if (
        response?.newOrdersAdminV2?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyOrderList(true);
      } else {
        setIsEmptyOrderList(false);
      }

      if (response?.newOrdersAdminV2?.data?.length < LIMIT) {
        setIsActive(false);
      }

      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.newOrdersAdminV2?.count,
      };
      setPaginationProp(pagination);
      paginationPropRef.current = pagination;

      saveRetainedFilterState(
        isAssignDelPartnerTab
          ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
          : TABLE_FILTER_KEYS.NEW_ORDERS,
        {
          paginationProp: pagination,
        },
      );

      setIsOrderLoading(false);
      setIsFetchMoreOrderLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const handleSort = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = { ...paginationProp, current: 1, skip: 0 };
    setIsOrderLoading(true);
    setIsAllSortPopoverOpen(false);
    setIsActive(true);
    setOrderList([]);
    const filteredSort = sort?.filter((item) => item?.value === values?.sort);
    if (filteredSort?.[0]?.field) {
      setSortedInfo({
        ...sortedInfo,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      });
      sortedInfoRef.current = {
        ...sortedInfoRef?.current,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      };
    }

    saveRetainedFilterState(
      isAssignDelPartnerTab
        ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
        : TABLE_FILTER_KEYS.NEW_ORDERS,
      {
        paginationProp: { ...paginationProp, current: 1, skip: 0 },
        sort: {
          ...sortedInfo,
          field: filteredSort?.[0]?.field,
          columnKey: filteredSort?.[0]?.field,
          order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
          column: filteredSort?.[0]?.field,
        },
      },
    );
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFilters?.pincodes,
          libraryIds: finalWhereFilters?.name,
          city: finalWhereFilters?.city,
        },
        sort: filteredSort?.[0]
          ? {
              field: filteredSort?.[0]?.field,
              order: filteredSort?.[0]?.sort,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const [orderItemsAdmin] = useLazyQuery(ORDER_ITEMS_ADMIN, {
    fetchPolicy: 'no-cache',
    onError() {},
  });

  const criticalOrdersRef = useRef(new Set());

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const stored = localStorage.getItem(CRITICAL_ORDERS_KEY);
    if (stored) {
      try {
        criticalOrdersRef.current = new Set(JSON.parse(stored));
      } catch (error) {
        criticalOrdersRef.current = new Set();
      }
    }
  }, []);

  const [users, { loading: isUsersLoading }] = useLazyQuery(USERS, {
    onCompleted: (response) => {
      if (response?.users?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.users?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.users?.data, (item) =>
        optionsCopy?.push({
          label: `${
            item?.firstName || item?.lastName
              ? `${item?.firstName || ''} ${item?.lastName || ''}`
              : '-'
          }`,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [pincodes, { loading: isPincodesLoading }] = useLazyQuery(PINCODES, {
    onCompleted: (response) => {
      if (response?.pincodes?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.pincodes?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.pincodes?.data, (item) =>
        optionsCopy?.push({
          label: item?.pincode,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [libraries, { loading: isLibrariesLoading }] = useLazyQuery(LIBRARIES, {
    onCompleted: (response) => {
      if (response?.libraries?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.libraries?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.libraries?.data, (item) =>
        optionsCopy?.push({ label: item?.name, key: item?.id }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [cities, { loading: isCityLoading }] = useLazyQuery(CITIES, {
    onCompleted: (response) => {
      if (response?.cities?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.cities?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.cities?.data, (item) =>
        optionsCopy?.push({ label: item?.name, key: item?.name }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [orderSelect, { loading: isOrderSelectLoading }] = useLazyQuery(
    ORDERS_SELECT,
    {
      onCompleted: (response) => {
        if (response?.newOrdersAdminV2?.data?.length < LIMIT) {
          setFilterIsEnd(true);
        }
        if (response?.newOrdersAdminV2?.count === 0) {
          setIsEmptyFilterList(true);
        } else {
          setIsEmptyFilterList(false);
        }
        const optionsCopy = [];
        forEach(response?.newOrdersAdminV2?.data, (item) =>
          optionsCopy?.push({
            label: item?.orderNumber,
            key: item?.id,
          }),
        );
        setFilterList([...filterList, ...optionsCopy]);
        setFilterLoading(false);
      },
      fetchPolicy: 'network-only',
      onError() {
        setFilterLoading(false);
      },
    },
  );

  const [cancelOrderAdmin] = useMutation(CANCEL_ORDER_ADMIN, {
    onError: () => {},
  });

  const [updateOrderStatusAdmin] = useMutation(UPDATE_ORDER_STATUS_ADMIN, {
    onError: () => {},
  });

  const [updateOrderMetadata] = useMutation(UPDATE_ORDER_METADATA, {
    onError: () => {},
  });

  const [updateOrderStatusAdminV2] = useMutation(UPDATE_ORDER_STATUS_ADMIN_V2, {
    onError: () => {},
  });

  const [assignDeliveryPartnerV2] = useMutation(ASSIGN_DELIVERY_PARTNER_V2, {
    onError: () => {},
  });

  const fetchOrders = () => {
    if (!hydrated || !initializedFromRetained) return; // NEW

    if (orderId?.current) {
      setOrderSearchTerm(orderId?.current);
      orderSearchTermRef.current = orderId?.current;

      saveRetainedFilterState(
        isAssignDelPartnerTab
          ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
          : TABLE_FILTER_KEYS.NEW_ORDERS,
        {
          searchTerm: orderId?.current,
        },
      );
    }
    isPollingRef.current = true;
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          skip:
            (paginationPropRef?.current?.current - 1) *
            (paginationPropRef?.current?.pageSize || 0),
          search: orderSearchTermRef?.current,
          limit: paginationPropRef?.current?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFiltersRef?.current?.orderNumber,
          deliveryType: finalWhereFiltersRef?.current?.deliveryType,
          orderTypes:
            finalWhereFiltersRef?.current?.orderTypes?.length > 0
              ? finalWhereFiltersRef?.current?.orderTypes
              : undefined,
          status:
            finalWhereFiltersRef?.current?.status?.length > 0
              ? finalWhereFiltersRef?.current?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFiltersRef?.current?.createdAt?.from?.$d,
            to: finalWhereFiltersRef?.current?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFiltersRef?.current?.deliveredOn?.from?.$d,
            to: finalWhereFiltersRef?.current?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
                      ?.$d,
                  to:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFiltersRef?.current?.pincodes,
          statusTiming: finalWhereFiltersRef?.current?.statusTiming,
          libraryIds: finalWhereFiltersRef?.current?.name,
          city: finalWhereFiltersRef?.current?.city,
          isPreOrder: finalWhereFiltersRef?.current?.isPreOrder,
          isDelayed: finalWhereFiltersRef?.current?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfoRef?.current?.column
          ? {
              field: sortedInfoRef?.current?.field,
              order:
                sortedInfoRef?.current?.order === 'ascend'
                  ? SORT?.ASC
                  : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  useEffect(() => {
    fetchOrders();
    const intervalId = setInterval(fetchOrders, ORDERS_POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [hydrated, initializedFromRetained]);

  const handleTagFilterChange = (tagIds) => {
    selectedTagIdsRef.current = tagIds;
    setSelectedTagIds(tagIds);
    setIsOrderLoading(true);
    setOrderList([]);
    setIsActive(true);
    setNestedData({});
    setExpandedRowKeys([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = {
      ...paginationPropRef.current,
      current: 1,
      skip: 0,
    };
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFiltersRef?.current?.orderNumber,
          search: orderSearchTermRef?.current,
          deliveryType: finalWhereFiltersRef?.current?.deliveryType,
          orderTypes:
            finalWhereFiltersRef?.current?.orderTypes?.length > 0
              ? finalWhereFiltersRef?.current?.orderTypes
              : undefined,
          status:
            finalWhereFiltersRef?.current?.status?.length > 0
              ? finalWhereFiltersRef?.current?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFiltersRef?.current?.createdAt?.from?.$d,
            to: finalWhereFiltersRef?.current?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFiltersRef?.current?.deliveredOn?.from?.$d,
            to: finalWhereFiltersRef?.current?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
                      ?.$d,
                  to:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFiltersRef?.current?.pincodes,
          statusTiming: finalWhereFiltersRef?.current?.statusTiming,
          libraryIds: finalWhereFiltersRef?.current?.name,
          city: finalWhereFiltersRef?.current?.city,
          isPreOrder: finalWhereFiltersRef?.current?.isPreOrder,
          isDelayed: finalWhereFiltersRef?.current?.isDelayed,
          orderTagIds: tagIds?.length > 0 ? tagIds : undefined,
        },
        sort: sortedInfoRef?.current?.column
          ? {
              field: sortedInfoRef?.current?.field,
              order:
                sortedInfoRef?.current?.order === 'ascend'
                  ? SORT?.ASC
                  : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const handleOrderScroll = () => {
    if (!isFetchMoreOrderLoading && !isOrderLoading) {
      isPollingRef.current = false;
      const currentLength = orderList?.length;
      setIsFetchMoreOrderLoading(true);
      newOrdersAdminV2({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip: currentLength,
            limit: paginationProp?.pageSize || LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            pincodeIds: finalWhereFilters?.pincodes,
            libraryIds: finalWhereFilters?.name,
            city: finalWhereFilters?.city,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
  };

  const infiniteScrollRef = useInfiniteScroll(handleOrderScroll, isActive);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    isInitialMountRef.current = true;
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    paginationPropRef.current = { ...paginationProp, ...pagination };
    setIsOrderLoading(true);
    setSortedInfo(sorter);
    saveRetainedFilterState(
      isAssignDelPartnerTab
        ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
        : TABLE_FILTER_KEYS.NEW_ORDERS,
      {
        paginationProp: { ...paginationProp, ...pagination },
        sort: sorter,
      },
    );
    sortedInfoRef.current = sorter;
    setNestedData({});
    setExpandedRowKeys([]);
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          skip,
          limit: pagination?.pageSize,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFilters?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
          libraryIds: finalWhereFilters?.name,
          city: finalWhereFilters?.city,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const isLastItem = (orderIds) => {
    const orderData = nestedData?.[orderIds]?.orderItemsAdmin?.data || [];
    const processedCount = orderData?.filter(
      (item) =>
        item?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
        item?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
        item?.status === FULFILLMENT_STATUS?.CANCELLED?.value,
    )?.length;

    const totalItems = orderData?.length;

    return processedCount + 1 >= totalItems;
  };

  const updateMainOrderList = (orderIds, commentData) => {
    setOrder((prev) => ({
      ...prev,
      metadata: {
        ...commentData,
      },
    }));

    setOrderList((prev) =>
      prev.map((orders) => {
        if (orders?.id === orderIds) {
          return {
            ...orders,
            metadata: {
              ...commentData,
            },
          };
        }
        return orders;
      }),
    );
  };

  const updateNestedDataLocally = (orderIds, orderItemId, newStatus) => {
    setNestedData((prev) => ({
      ...prev,
      [orderIds]: {
        ...prev?.[orderIds],
        orderItemsAdmin: {
          ...prev?.[orderIds]?.orderItemsAdmin,
          data:
            prev?.[orderIds]?.orderItemsAdmin?.data?.map((item) =>
              item?.id === orderItemId
                ? {
                    ...item,
                    status: newStatus,
                    cancellationData:
                      newStatus === FULFILLMENT_STATUS.UNFULFILLED?.value
                        ? { ...tempUnfulfilledReason.current }
                        : null,
                  }
                : item,
            ) || [],
        },
      },
    }));
  };

  const handleUserClick = (record) => {
    dispatch({
      type: 'SET_END_USER_DRAWER_VISIBLE',
      data: true,
    });
    dispatch({
      type: 'SET_END_USER_DETAILS',
      data: {
        userNumber: record?.userNumber,
        id: record?.id,
        onboardedOn: record?.createdAt,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setOrderSearchTerm(trimValue);
    orderId.current = trimValue;
    orderSearchTermRef.current = trimValue;
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = { ...paginationProp, current: 1, skip: 0 };
    saveRetainedFilterState(
      isAssignDelPartnerTab
        ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
        : TABLE_FILTER_KEYS.NEW_ORDERS,
      {
        paginationProp: { ...paginationProp, skip: 0, current: 1 },
        searchTerm: trimValue,
      },
    );

    setIsOrderLoading(true);
    setOrderList([]);
    setNestedData({});
    setExpandedRowKeys([]);
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: trimValue,
          deliveryType: finalWhereFilters?.deliveryType,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          statusTiming: finalWhereFilters?.statusTiming,
          pincodeIds: finalWhereFilters?.pincodes,
          libraryIds: finalWhereFilters?.name,
          city: finalWhereFilters?.city,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleAddCommentInOrder = async (values) => {
    setIsAddCommentLoading(true);
    const response = await cancelOrderAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          additionalComment: values?.comment?.trim(),
          reason: values?.reason,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsAddCommentModalOpen(false);
      setNestedData({});
      setExpandedRowKeys([]);
      setOrderList([]);
      form?.resetFields();
      newOrdersAdminV2({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            statusTiming: finalWhereFilters?.statusTiming,
            pincodeIds: finalWhereFilters?.pincodes,
            libraryIds: finalWhereFilters?.name,
            city: finalWhereFilters?.city,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsAddCommentLoading(false);
  };

  const handleAddCommentInOrderItem = async (values) => {
    setIsAddCommentLoading(true);
    const response = await updateOrderStatusAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          orderItemId: orderItem?.id,
          additionalComment: values?.comment?.trim(),
          reason: values?.reason,
          status: FULFILLMENT_STATUS?.UNFULFILLED?.value,
        },
      },
    });
    if (response?.data) {
      if (isLastItem(order?.id, orderItem?.id)) {
        setIsAllOrderItemModalOpen(false);
        setIsOrderLoading(true);
        setNestedData({});
        setExpandedRowKeys([]);
        setOrderList([]);
        form?.resetFields();
        newOrdersAdminV2({
          variables: {
            filter: {
              ...initialNewDeliveryFilter,
              skip:
                (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
              limit: paginationProp?.pageSize || LIMIT,
              orderType: ORDER_TYPE?.NEW,
              orderIds: finalWhereFilters?.orderNumber,
              search: orderSearchTerm,
              deliveryType: finalWhereFilters?.deliveryType,
              orderTypes:
                finalWhereFilters?.orderTypes?.length > 0
                  ? finalWhereFilters?.orderTypes
                  : undefined,
              status:
                finalWhereFilters?.status?.length > 0
                  ? finalWhereFilters?.status
                  : statusFilter,
              dateRange: {
                from: finalWhereFilters?.createdAt?.from?.$d,
                to: finalWhereFilters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: finalWhereFilters?.deliveredOn?.from?.$d,
                to: finalWhereFilters?.deliveredOn?.to?.$d,
              },
              ...(finalWhereFilters?.expectedDeliveryDate?.from
                ? {
                    expectedDeliveryDate: {
                      from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                      to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                    },
                  }
                : {}),
              statusTiming: finalWhereFilters?.statusTiming,
              pincodeIds: finalWhereFilters?.pincodes,
              libraryIds: finalWhereFilters?.name,
              city: finalWhereFilters?.city,
              isPreOrder: finalWhereFilters?.isPreOrder,
              isDelayed: finalWhereFilters?.isDelayed,
              orderTagIds:
                selectedTagIdsRef?.current?.length > 0
                  ? selectedTagIdsRef.current
                  : undefined,
            },
            sort: sortedInfo?.column
              ? {
                  field: sortedInfo?.field,
                  order:
                    sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
                }
              : initialNewDeliverySort,
          },
        });
      } else {
        tempUnfulfilledReason.current = {
          reason: values?.reason,
          additionalComment: values?.comment?.trim(),
          type: CANCELLATION_TYPE?.LIBRARY?.value,
          date: Date(),
        };

        updateNestedDataLocally(
          order.id,
          orderItem.id,
          FULFILLMENT_STATUS?.UNFULFILLED?.value,
        );
      }
    }
    form?.resetFields();
    setIsAddCommentLoading(false);
    setIsAddCommentModalOpen(false);
  };

  const handleFulfillOrderItem = async () => {
    setIsFulfillLoading(true);
    const response = await updateOrderStatusAdmin({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          orderItemId: orderItem?.id,
          status: FULFILLMENT_STATUS?.FULFILLED?.value,
        },
      },
    });
    if (response?.data) {
      if (isLastItem(order?.id, orderItem?.id)) {
        setIsAllOrderItemModalOpen(false);
        setIsOrderLoading(true);
        setNestedData({});
        setOrderList([]);
        setExpandedRowKeys([]);
        newOrdersAdminV2({
          variables: {
            filter: {
              ...initialNewDeliveryFilter,
              skip:
                (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
              limit: paginationProp?.pageSize || LIMIT,
              orderType: ORDER_TYPE?.NEW,
              orderIds: finalWhereFilters?.orderNumber,
              search: orderSearchTerm,
              deliveryType: finalWhereFilters?.deliveryType,
              orderTypes:
                finalWhereFilters?.orderTypes?.length > 0
                  ? finalWhereFilters?.orderTypes
                  : undefined,
              status:
                finalWhereFilters?.status?.length > 0
                  ? finalWhereFilters?.status
                  : statusFilter,
              dateRange: {
                from: finalWhereFilters?.createdAt?.from?.$d,
                to: finalWhereFilters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: finalWhereFilters?.deliveredOn?.from?.$d,
                to: finalWhereFilters?.deliveredOn?.to?.$d,
              },
              ...(finalWhereFilters?.expectedDeliveryDate?.from
                ? {
                    expectedDeliveryDate: {
                      from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                      to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                    },
                  }
                : {}),
              statusTiming: finalWhereFilters?.statusTiming,
              pincodeIds: finalWhereFilters?.pincodes,
              libraryIds: finalWhereFilters?.name,
              city: finalWhereFilters?.city,
              isPreOrder: finalWhereFilters?.isPreOrder,
              isDelayed: finalWhereFilters?.isDelayed,
              orderTagIds:
                selectedTagIdsRef?.current?.length > 0
                  ? selectedTagIdsRef.current
                  : undefined,
            },
            sort: sortedInfo?.column
              ? {
                  field: sortedInfo?.field,
                  order:
                    sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
                }
              : initialNewDeliverySort,
          },
        });
      } else {
        updateNestedDataLocally(
          order.id,
          orderItem.id,
          FULFILLMENT_STATUS?.FULFILLED?.value,
        );

        if (tempComment?.current?.[order?.id])
          updateMainOrderList(order?.id, tempComment?.current?.[order?.id]);
      }
      setIsDelayReasonPrompts(false);
      delayForm?.resetFields();
      setIsReadyLoading(false);
    }
    setIsFulfillLoading(false);
  };

  const handleReadyOrder = async (values) => {
    setIsReadyLoading(true);
    const response = await updateOrderStatusAdminV2({
      variables: {
        where: {
          id: values,
        },
        data: {
          status: ORDER_STATUS?.CONFIRMED?.value,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsReadyPrompts(false);
      setNestedData({});
      setOrderList([]);
      setSelectedRowKeys([]);
      setExpandedRowKeys([]);
      setIsConfirmPrompts(false);
      newOrdersAdminV2({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            statusTiming: finalWhereFilters?.statusTiming,
            pincodeIds: finalWhereFilters?.pincodes,
            libraryIds: finalWhereFilters?.name,
            city: finalWhereFilters?.city,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsReadyLoading(false);
    setIsDelayReasonPrompts(false);
    delayForm?.resetFields();
  };
  const handleAddDelayReason = async (values) => {
    setIsReadyLoading(true);
    const metadataResponse = await updateOrderMetadata({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          metadata: [
            { key: 'delayReason', value: values?.reason },
            { key: 'delayNote', value: values?.comment },
          ],
        },
      },
    });
    if (metadataResponse?.data) {
      tempComment.current = {
        [order?.id]: {
          delayReason: { value: values?.reason },
          delayNote: { value: values?.comment },
        },
      };
      if (isOrderAction) {
        handleReadyOrder([order?.id]);
      } else {
        handleFulfillOrderItem();
      }
    } else {
      setIsReadyLoading(false);
    }
  };

  const handleAssignDeliveryPartner = async (rowKeys, deliveryPartner) => {
    setIsDeliveryPartnerLoading(true);
    const response = await assignDeliveryPartnerV2({
      variables: {
        where: {
          id: rowKeys,
        },
        data: {
          deliveryPartnerId: deliveryPartner,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setDeliveryPartnerModalOpen(false);
      setNestedData({});
      setOrderList([]);
      setExpandedRowKeys([]);
      setSelectedRowKeys([]);
      setIsDeliveryPartnerPrompts(false);
      setDeliveryPartnerData({});
      setExistingDeliveryPartnerData({});
      existingDelPartnerRef.current = null;
      assignDelPartnerForm.resetFields();
      newOrdersAdminV2({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            statusTiming: finalWhereFilters?.statusTiming,
            pincodeIds: finalWhereFilters?.pincodes,
            libraryIds: finalWhereFilters?.name,
            city: finalWhereFilters?.city,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsDeliveryPartnerLoading(false);
  };

  const getFilterData = (confirm) => {
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);

    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          search: orderSearchTerm,
          orderType: ORDER_TYPE?.NEW,
          orderIds: whereFilter?.orderNumber,
          deliveryType: whereFilter?.deliveryType,
          orderTypes:
            whereFilter?.orderTypes?.length > 0
              ? whereFilter?.orderTypes
              : undefined,
          status:
            whereFilter?.status?.length > 0
              ? whereFilter?.status
              : statusFilter,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: whereFilter?.deliveredOn?.from?.$d,
            to: whereFilter?.deliveredOn?.to?.$d,
          },
          ...(whereFilter?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: whereFilter?.expectedDeliveryDate?.from?.$d,
                  to: whereFilter?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: whereFilter?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
          libraryIds: whereFilter?.name,
          city: whereFilter?.city,
          isPreOrder: whereFilter?.isPreOrder,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    finalWhereFiltersRef.current = { ...finalWhereFilters, ...whereFilter };
    setExportFilter({ ...exportFilter, ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    paginationPropRef.current = { ...paginationProp, skip: 0, current: 1 };

    saveRetainedFilterState(
      isAssignDelPartnerTab
        ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
        : TABLE_FILTER_KEYS.NEW_ORDERS,
      {
        filters: { ...finalWhereFilters, ...whereFilter },
        paginationProp: { ...paginationProp, skip: 0, current: 1 },
      },
    );
    if (confirm) {
      confirm();
    }
  };

  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    let whereFiltersCopy;
    if (dataIndex === 'deliveryType') {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: null,
      };
    } else {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: [],
      };
    }
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    finalWhereFiltersRef.current = {
      ...finalWhereFilters,
      ...whereFiltersCopy,
    };
    setExportFilter({
      ...exportFilter,
      ...finalWhereFilters,
      ...whereFiltersCopy,
    });
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          search: orderSearchTerm,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: whereFiltersCopy?.orderNumber,
          deliveryType: whereFiltersCopy?.deliveryType,
          orderTypes:
            whereFiltersCopy?.orderTypes?.length > 0
              ? whereFiltersCopy?.orderTypes
              : undefined,
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : statusFilter,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: whereFiltersCopy?.deliveredOn?.from?.$d,
            to: whereFiltersCopy?.deliveredOn?.to?.$d,
          },
          ...(whereFiltersCopy?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: whereFiltersCopy?.expectedDeliveryDate?.from?.$d,
                  to: whereFiltersCopy?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          libraryIds: whereFiltersCopy?.name,
          statusTiming: finalWhereFilters?.statusTiming,
          pincodeIds: whereFiltersCopy?.pincodes,
          city: whereFiltersCopy?.city,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    paginationPropRef.current = { ...paginationProp, skip: 0, current: 1 };
    saveRetainedFilterState(
      isAssignDelPartnerTab
        ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
        : TABLE_FILTER_KEYS.NEW_ORDERS,
      {
        filters: { ...finalWhereFilters, ...whereFiltersCopy },
        paginationProp: { ...paginationProp, skip: 0, current: 1 },
        filterTags: { ...filterTags, ...tagsCopy },
      },
    );

    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      saveRetainedFilterState(TABLE_FILTER_KEYS.NEW_ORDERS, {
        ...filterTags,
        ...finalFilterTags,
      });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      setFilterIsEnd(false);
      const optionsCopy = [];
      const orderStatus = map(
        filter(ORDER_STATUS, (item) =>
          isAssignDelPartnerTab
            ? ORDER_RIDER_REASSIGNABLE_STATUSES?.includes(item?.value)
            : item?.value === ORDER_STATUS?.PLACED?.value ||
              item?.value === ORDER_STATUS?.CONFIRMED?.value,
        ),
        (item) => item,
      );
      switch (filterIndex) {
        case 'deliveryPartnerName':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: [USER_ROLES?.DELIVERY?.key],
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'users':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: [USER_ROLES?.USER?.key],
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'pincodes':
          pincodes({
            variables: {
              filter: {
                ...initialTableFilter,
              },
              sort: {
                field: 'pincode',
                order: 'ASC',
              },
            },
          });
          break;

        case 'orderTypes':
          forEach(ORDER_TYPE_FILTER_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'deliveryType':
          forEach(DELIVERY_TYPE_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'orderNumber':
          orderSelect({
            variables: {
              filter: {
                ...initialTableFilter,
                orderType: ORDER_TYPE?.NEW,
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'name':
          libraries({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'city':
          cities({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'status':
          forEach(orderStatus, (item) => {
            optionsCopy?.push({ label: item?.text, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      saveRetainedFilterState(TABLE_FILTER_KEYS.NEW_ORDERS, {
        filterTags: finalFilterTags,
      });
      setWhereFilter(finalWhereFilters);
      setFilterSearch('');
    }
  }, [filterVisible]);

  const onFilterScroll = (event, dataIndex) => {
    if (dataIndex === 'status' || dataIndex === 'orderTypes') {
      return;
    }
    if (filterScrollDebounceJob) {
      filterScrollDebounceJob?.cancel();
      filterScrollDebounceJob = null;
    }
    if (
      !isUsersLoading &&
      !isPincodesLoading &&
      !isOrderSelectLoading &&
      !isLibrariesLoading &&
      !isCityLoading &&
      !filterIsEnd
    ) {
      const target = event?.target;
      const currentLength = filterList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        filterScrollDebounceJob = debounce(() => {
          setFilterLoading(true);
          switch (dataIndex) {
            case 'deliveryPartnerName':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: [USER_ROLES?.DELIVERY?.key],
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'users':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: [USER_ROLES?.USER?.key],
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'pincodes':
              pincodes({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: {
                    field: 'pincode',
                    order: 'ASC',
                  },
                },
              });
              break;

            case 'orderNumber':
              orderSelect({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    orderType: ORDER_TYPE?.NEW,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'name':
              libraries({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'city':
              cities({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            default:
              break;
          }
        }, 500);
        filterScrollDebounceJob();
      }
    }
  };

  const handleFilterSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setFilterIsEnd(false);
    setFilterLoading(true);
    setFilterList([]);
    switch (dataIndex) {
      case 'deliveryPartnerName':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: [USER_ROLES?.DELIVERY?.key],
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'users':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: [USER_ROLES?.USER?.key],
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'pincodes':
        pincodes({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: {
              field: 'pincode',
              order: 'ASC',
            },
          },
        });
        break;

      case 'orderNumber':
        orderSelect({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              orderType: ORDER_TYPE?.NEW,
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'name':
        libraries({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'city':
        cities({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      default:
        break;
    }
  };

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
    saveRetainedFilterState(TABLE_FILTER_KEYS.NEW_ORDERS, newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
    saveRetainedFilterState(TABLE_FILTER_KEYS.NEW_ORDERS, newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: value,
    };
    if (!value?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex, showSearch = true) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (
        dataIndex === 'createdAt' ||
        dataIndex === 'deliveredOn' ||
        dataIndex === 'expectedDeliveryDate'
      ) {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'deliveryType') {
        return (
          <div className="custom-filter-dropdown">
            <Group
              options={DELIVERY_TYPE_OPTIONS}
              value={whereFilter?.[dataIndex]}
              onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {showSearch && (
              <SearchComponent
                className="filter-search"
                id="search-container-id-roles"
                placeholder="Search..."
                name={dataIndex}
                query={filterSearch}
                setQuery={setFilterSearch}
                getData={(value) => handleFilterSearch(value, dataIndex)}
              />
            )}
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div
              className="filter-checkboxes"
              onScroll={(e) => onFilterScroll(e, dataIndex)}
            >
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (isVisible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(isVisible);
    },
  });

  const handleExpand = async (expanded, record) => {
    setIsLoading({
      [record?.id]: true,
    });
    setIsDisabled(true);
    if (!nestedData?.[record?.id]) {
      const res = await orderItemsAdmin({
        variables: {
          where: {
            id: record?.id,
          },
        },
      });
      if (res?.data) {
        setNestedData((state) => ({
          ...state,
          [record?.id]: res?.data,
        }));
      }
      setIsDisabled(false);
    }
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record?.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys?.filter((key) => key !== record?.id));
    }
    setIsLoading({
      [record?.id]: false,
    });
    setIsDisabled(false);
  };

  const handleOrderChange = (value, record) => {
    setOrder(record);
    setIsOrderAction(true);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      setIsReadyPrompts(true);
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const handleOrderItemChange = (value, record, orderDetails) => {
    setOrderItem(record);
    setOrder(orderDetails);
    setIsOrderAction(false);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      if (isUnitsFeatureEnabledForLibrary(orderDetails?.library?.id)) {
        setIsUnitScanOpen(true);
      } else {
        setIsFulfillPrompts(true);
      }
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const hasSelected = selectedRowKeys.length > 0;
  const hasAdloggsLibrarySelected = selectedRowKeys.some((id) =>
    ADLOGGS_OPEN_LIBRARY_IDS.has(selectedOrdersById?.[id]?.library?.id),
  );
  const renderBlinkingDotEl = (orderStatus) => {
    if (isAssignDelPartnerTab && orderStatus === ORDER_STATUS.CONFIRMED.value) {
      return (
        <Badge status="processing" color="red" className="custom-badge mr-8" />
      );
    }
    if (!isAssignDelPartnerTab && orderStatus === ORDER_STATUS.PLACED.value) {
      return (
        <Badge status="processing" color="red" className="custom-badge mr-8" />
      );
    }
    return null;
  };

  const renderSLA = (record) => {
    if (isAssignDelPartnerTab) {
      if (
        record?.status === ORDER_STATUS.READY_TO_SHIP.value ||
        !record?.nextStatusTime
      ) {
        return <div>N/A</div>;
      }

      if (record?.nextStatusTime) {
        return (
          <div>
            {formatDate(record?.nextStatusTime, DATE_FORMATS?.WITH_TIME)}
          </div>
        );
      }
    } else if (record?.nextStatusTime) {
      return (
        <div>{formatDate(record?.nextStatusTime, DATE_FORMATS?.WITH_TIME)}</div>
      );
    } else {
      return <div>N/A</div>;
    }
  };

  const [getReadyToShipOrders] = useLazyQuery(GET_READY_TO_SHIP_ORDERS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const checkDeliveryPartner = async (userData, recordData) => {
    setOrder(recordData);
    setDeliveryPartnerData(userData);
    const respose = await getReadyToShipOrders({
      variables: {
        where: {
          orderId: recordData?.id,
          userId: recordData?.user?.id,
        },
      },
    });

    if (
      respose?.data?.getReadyToShipOrder?.deliveryPartner?.id &&
      respose?.data?.getReadyToShipOrder?.deliveryPartner?.id !==
        userData?.value
    ) {
      const existingDelPartner =
        respose?.data?.getReadyToShipOrder?.deliveryPartner;

      setExistingDeliveryPartnerData({
        children: `${existingDelPartner?.firstName || ''} ${
          existingDelPartner?.lastName || ''
        } (${existingDelPartner?.userNumber})`,
        key: existingDelPartner?.id,
        value: existingDelPartner?.id,
      });
    }
    setIsDeliveryPartnerPrompts(true);
    setIsDeliveryPartnerPrompts(true);
  };

  const columns = [
    {
      title: 'ORDER ID',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left',
      ellipsis: true,
      fixed: 'left',
      className: 'max-width-column',
      sorter: true,
      width: 150,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (_, record) => (
        <div className="d-flex align-center">
          {renderBlinkingDotEl(record?.status)}
          <RenderCopyIcon copyText={record?.orderNumber} />
          {record?.orderNumber || '-'}
          <div onClick={(e) => e?.stopPropagation()}>
            {record?.isExpressDelivery && !record?.isQuickDelivery && (
              <Tooltip
                title={EXPRESS_DELIVERY_TOOLTIP_TEXT}
                placement="top"
                zIndex={4}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Tag
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="ml-8 mr-0 express-delivery-icon"
                >
                  {EXPRESS_DELIVERY_EMOJI}
                </Tag>
              </Tooltip>
            )}
            {record?.isQuickDelivery && (
              <Tooltip
                title={QUICK_DELIVERY_TOOLTIP_TEXT}
                placement="top"
                zIndex={4}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Tag color="yellow" className="ml-8 mr-0 quick-delivery-icon">
                  {QUICK_DELIVERY_EMOJI}
                </Tag>
              </Tooltip>
            )}
          </div>
          {record?.isFirstOrder && (
            <StatusFlag type={ORDER_TAG_TYPES.FIRST_ORDER} />
          )}
          {record?.delayedInConfirmed && (
            <StatusFlag type={ORDER_TAG_TYPES.DELAYED_CONFIRMED} />
          )}
          {record?.metadata?.isContainBigToy && <BigToyIcon />}
          {hasPreOrderToy(record) && (
            <StatusFlag type={ORDER_TAG_TYPES.PRE_ORDER} />
          )}
          <OrderDynamicTags tags={record?.orderTags} />
        </div>
      ),
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      key: 'quantity',
      ellipsis: true,
      align: 'left',
      width: 100,
      className: 'max-width-column',
      render: (_, record) => record?.orderItemCount || '-',
    },
    {
      title: 'TOTAL ORDERS COUNT',
      dataIndex: 'totalOrdersCount',
      key: 'totalOrdersCount',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'totalOrdersCount' && sortedInfo?.order,
      render: (_, record) => record?.totalOrdersCount ?? '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={ORDER_STATUS?.[record?.status]?.color}
          text={ORDER_STATUS?.[record?.status]?.text}
        />
      ),
      ...(isAssignDelPartnerTab ? filterPopup('status', false) : {}),
    },
    {
      title: 'ORDER TYPE',
      dataIndex: 'orderType',
      key: 'orderType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.orderType || '-',
      ...filterPopup('orderTypes', false),
    },
    {
      title: isAssignDelPartnerTab
        ? 'RIDER ASSIGNMENT SLA'
        : 'ORDER PACKAGING SLA',
      dataIndex: 'orderPackagingSLA',
      key: 'orderPackagingSLA',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => <div>{renderSLA(record)}</div>,
    },
    {
      title: isAssignDelPartnerTab
        ? 'RIDER ASSIGNMENT TIME LEFT'
        : 'ORDER PACKAGING TIME LEFT',
      dataIndex: 'timeLeft',
      key: 'timeLeft',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const nextStatusTime = new Date(record?.nextStatusTime)?.getTime();
        return (
          <div>
            {
              // eslint-disable-next-line no-nested-ternary
              isAssignDelPartnerTab ? (
                record?.status !== ORDER_STATUS?.CONFIRMED.value ||
                !record?.nextStatusTime ? (
                  'N/A' // or whatever you want to show for non-confirmed statuses
                ) : (
                  <Countdown date={nextStatusTime} renderer={renderer} />
                )
              ) : !isEmpty(record?.nextStatusTime) ? (
                <Countdown date={nextStatusTime} renderer={renderer} />
              ) : (
                'N/A'
              )
            }
          </div>
        );
      },
    },
    {
      title: 'DELIVERY SLA',
      dataIndex: 'deliverySLA',
      key: 'deliverySLA',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        record?.nextStatusTime &&
        record?.status === ORDER_STATUS?.READY_TO_SHIP.value
          ? formatDate(record?.nextStatusTime, DATE_FORMATS?.WITH_TIME)
          : 'N/A',
    },
    {
      title: 'DELIVERY TIME LEFT',
      dataIndex: 'deliveryTimeLeft',
      key: 'deliveryTimeLeft',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const nextStatusTime = new Date(record.nextStatusTime)?.getTime();
        return (
          <div>
            {!isEmpty(record?.nextStatusTime) &&
            record?.status === ORDER_STATUS?.READY_TO_SHIP.value ? (
              <Countdown date={nextStatusTime} renderer={renderer} />
            ) : (
              'N/A'
            )}
          </div>
        );
      },
    },
    {
      title: 'DELIVERY TYPE',
      dataIndex: 'deliveryType',
      key: 'deliveryType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) =>
        DELIVERY_TYPE?.[record?.deliveryType]?.label || '-',
      ...filterPopup('deliveryType'),
    },
    {
      title: 'USER NAME',
      dataIndex: 'userName',
      key: 'userName',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: (e) =>
          isUserPermission &&
          record?.user?.id &&
          handleOpenUserDrawer(e, record?.user, dispatch),
        className: `${
          isUserPermission && record?.user?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const userData = record?.user;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{`${
                userData?.firstName ? userData?.firstName : ''
              } ${userData?.lastName ? userData?.lastName : ''}`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'LIBRARY NAME',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      align: 'left',
      ellipsis: true,
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () => {
          const library = record?.library;
          return (
            isLibraryPermission &&
            library?.id &&
            history?.push(
              `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${library?.id}/${library?.libraryNumber}`,
            )
          );
        },
        className: `${
          isLibraryPermission && record?.library?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          <div className="table-data-with-id">{record?.library?.name}</div>
          <div className="font-size-12">({record?.library?.libraryNumber})</div>
        </>
      ),
      ...filterPopup('name'),
    },
    {
      title: 'CITY',
      dataIndex: 'city',
      key: 'city',
      width: 160,
      align: 'left',
      ellipsis: true,
      className: `max-width-column`,
      render: (_, record) => (
        <>
          <div>{record?.library?.city || '-'}</div>
        </>
      ),
      ...filterPopup('city'),
    },
    {
      title: 'PINCODE',
      dataIndex: 'pincode',
      key: 'pincode',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.pincode?.pincode || '-',
      ...filterPopup('pincodes'),
    },
    {
      title: 'DELAY REASON',
      dataIndex: 'delayReason',
      key: 'delayReason',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div className="action-button" onClick={(e) => e?.stopPropagation()}>
          {!isEmpty(record?.metadata?.delayReason) ? (
            <Tooltip title="View reason" placement="top" zIndex={4}>
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  setIsReasonPrompt(true);
                  setOrder(record);
                  setIsOrderAction(true);
                }}
              />
            </Tooltip>
          ) : (
            <span className="mr-24">-</span>
          )}
        </div>
      ),
    },

    {
      title: 'DELIVERY PARTNER NAME',
      dataIndex: 'deliveryPartnerName',
      key: 'deliveryPartnerName',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      onCell: (record) => ({
        onClick: () =>
          isDeliveryPartnerPermission &&
          record?.deliveryPartner?.id &&
          history?.push(
            `${ROUTES?.SYSTEM_USERS}${ROUTES?.DELIVERY_PARTNERS}/view/${record?.deliveryPartner?.id}`,
          ),
        className: `${
          isUserPermission &&
          record?.deliveryPartner?.id &&
          'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          {record?.deliveryPartner?.firstName ||
          record?.deliveryPartner?.lastName ? (
            <div className="table-data-with-id">{`${
              record?.deliveryPartner?.firstName
                ? record?.deliveryPartner?.firstName
                : ''
            } ${
              record?.deliveryPartner?.lastName
                ? record?.deliveryPartner?.lastName
                : ''
            }`}</div>
          ) : (
            '-'
          )}
          {record?.deliveryPartner?.userNumber ? (
            <div className="font-size-12">
              ({record?.deliveryPartner?.userNumber})
            </div>
          ) : (
            ''
          )}
        </>
      ),
    },

    {
      title: 'PLACED ON',
      dataIndex: 'placedOn',
      key: 'placedOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.WITH_TIME),
      ...filterPopup('createdAt'),
    },
    {
      title: 'ESTIMATED SHIPPING DATE',
      dataIndex: 'deliveredOn',
      key: 'deliveredOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.deliveredOn, DATE_FORMATS?.WITH_TIME) || '-',
      ...filterPopup('deliveredOn'),
    },
    {
      title: 'EXPECTED DELIVERY DATE',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'expectedDeliveryDate' && sortedInfo?.order,
      render: (_, record) =>
        formatDate(record?.expectedDeliveryDate, DATE_FORMATS?.WITH_TIME) ||
        '-',
      ...filterPopup('expectedDeliveryDate'),
    },

    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width: 250,
      render: (_, record) => {
        if (isAssignDelPartnerTab) {
          if (
            record?.deliveryService === DELIVERY_PARTNER?.ADLOGGS?.NAME ||
            record?.metadata?.deliveryStaffDetails
          ) {
            const adloggsUserData = {
              firstName:
                record?.metadata?.deliveryStaffDetails?.name || 'Adloggs Agent',
              lastName: '',
              id: DELIVERY_PARTNER?.ADLOGGS?.PLACEHOLDER_ID,
              userNumber: record?.metadata?.deliveryStaffDetails?.phone,
            };
            return (
              <div onClick={(e) => e?.stopPropagation()}>
                <UserSelect
                  size="small"
                  className="user-select"
                  placeholder="Select Delivery Partner"
                  showAction={['click']}
                  value={adloggsUserData.id}
                  userData={adloggsUserData}
                  roles={USER_ROLES?.DELIVERY?.key}
                  allowClear={false}
                  disabled
                />
              </div>
            );
          }
          const { firstName, lastName, id, userNumber } =
            record?.deliveryPartner || {};
          const assignedDeliveryPartner = {
            firstName,
            lastName,
            id,
            userNumber,
          };
          return (
            <div onClick={(e) => e?.stopPropagation()}>
              <UserSelect
                size="small"
                className="user-select"
                placeholder="Select Delivery Partner"
                showAction={['click']}
                value={id || null}
                userData={id ? assignedDeliveryPartner : null}
                roles={USER_ROLES?.DELIVERY?.key}
                onChange={(value, userData) => {
                  checkDeliveryPartner(userData, record);
                }}
                allowClear={false}
                disabled={ADLOGGS_OPEN_LIBRARY_IDS.has(record?.library?.id)}
              />
            </div>
          );
        }

        if (isUnitsFeatureEnabledForLibrary(record?.library?.id)) {
          return '-';
        }

        let selectedValue = null;
        if (
          record?.status === ORDER_STATUS?.CANCELLED?.value ||
          record?.status === ORDER_STATUS?.CONFIRMED?.value
        ) {
          if (record?.status === ORDER_STATUS?.CANCELLED?.value) {
            selectedValue = FULFILLMENT_STATUS?.UNFULFILLED?.value;
          } else {
            selectedValue = FULFILLMENT_STATUS?.FULFILLED?.value;
          }
        } else {
          selectedValue = null;
        }
        return (
          <div onClick={(e) => e?.stopPropagation()}>
            <SelectComponent
              size="small"
              className="full-width"
              placeholder="Select Action"
              showAction={['click']}
              value={selectedValue}
              showSearch={false}
              disabled={
                !!selectedValue ||
                (record.transferOrderItemCount !== 0 &&
                  record.transferOrderItemCount !== null)
              }
              disableFulfill={DISABLE_ORDER}
              record={record}
              options={NEW_ORDER_TABLE_ACTIONS_OPTIONS}
              onSelect={(value) => handleOrderChange(value, record)}
              allowClear={false}
            />
          </div>
        );
      },
    },
  ]?.filter((item) => {
    if (
      !isAssignDelPartnerTab &&
      (item?.dataIndex === 'deliveryPartnerName' ||
        item?.dataIndex === 'deliveryTimeLeft' ||
        item?.dataIndex === 'deliverySLA')
    ) {
      return item?.hidden;
    }

    if (isAssignDelPartnerTab && item?.dataIndex === 'delayReason') {
      return item?.hidden;
    }

    if (isAssignDelPartnerTab && item?.dataIndex === 'totalOrdersCount') {
      return item?.hidden;
    }
    return !item?.hidden;
  });
  const expandedRowRender = (data) => {
    const orderItemList = nestedData?.[data?.id]?.orderItemsAdmin?.data;
    const nestedColumns = [
      {
        title: 'ORDER ID',
        dataIndex: 'orderNumber',
        key: 'orderNumber',
        ellipsis: true,
        align: 'left',
        fixed: 'left',
        className: 'max-width-column',
        width: 100,
        render: (_, record) => (
          <div className="d-flex align-center">
            <RenderCopyIcon copyText={record?.order?.orderNumber} />
            {record?.order?.orderNumber || '-'}
          </div>
        ),
      },
      {
        title: 'PRODUCT NAME',
        dataIndex: 'productName',
        key: 'productName',
        ellipsis: true,
        align: 'left',
        fixed: 'left',
        width: 250,
        className: 'max-width-column',
        onCell: (record) => ({
          onClick: () =>
            isToyPermission &&
            record?.toy?.id &&
            openToyDrawer(record?.toy?.id),
          className: `${
            isToyPermission && record?.toy?.id && 'pointer table-cell'
          }`,
        }),
        render: (_, record) => (
          <>
            <div className="table-data-with-id d-flex align-center">
              {record?.toy?.name || '-'}
            </div>
            {record?.toy?.toyNumber && (
              <div className="font-size-12">({record?.toy?.toyNumber})</div>
            )}
          </>
        ),
      },
      {
        title: 'MAPPED UNIT',
        dataIndex: 'inventoryUnits',
        key: 'inventoryUnits',
        width: 200,
        align: 'left',
        className: 'max-width-column',
        onCell: () => ({
          onClick: (e) => e?.stopPropagation(),
        }),
        render: (_, record) => (
          <MappedUnitsCell units={record?.inventoryUnits} />
        ),
      },
      {
        title: 'IMAGES',
        dataIndex: 'images',
        key: 'images',
        ellipsis: true,
        width: 100,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => {
          const images = record?.toy?.images;
          return (
            <span>
              {images?.length > 0 ? (
                <>
                  <Image
                    preview={{
                      visible: false,
                    }}
                    width={20}
                    height={20}
                    src={images?.[0]?.url}
                    onClick={(e) => {
                      e?.stopPropagation();
                      setVisible(true);
                      setToyImages(images);
                    }}
                    className="pointer"
                  />
                </>
              ) : (
                <span>No Images</span>
              )}
              {images?.length > 1 ? ` + ${images?.length - 1}` : ''}
            </span>
          );
        },
      },
      {
        title: 'BRAND',
        dataIndex: 'brand',
        key: 'brand',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => record?.toy?.brand?.name || '-',
      },
      {
        title: 'STATUS',
        dataIndex: 'status',
        key: 'status',
        ellipsis: true,
        width: 180,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => {
          const config =
            FULFILLMENT_STATUS?.[record?.status] ||
            ORDER_STATUS?.[record?.status];
          if (!record?.status) return '-';
          return (
            <Badge
              color={config?.color}
              text={config?.text || humanizeEnum(record?.status)}
            />
          );
        },
      },
      {
        title: 'STOCK AT BOOKING',
        dataIndex: 'stockAtBooking',
        key: 'stockAtBooking',
        align: 'left',
        width: 150,
        render: (_, record) => record?.stockAtBooking ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="AVAILABLE FOR BOOKING"
            info={STOCK_COLUMN_TOOLTIPS.AVAILABLE_FOR_BOOKING}
          />
        ),
        dataIndex: 'availableStock',
        key: 'availableStock',
        align: 'left',
        width: 220,
        render: (_, record) => record?.libraryInventory?.availableStock ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="PHYSICALLY AVAILABLE STOCK"
            info={STOCK_COLUMN_TOOLTIPS.PHYSICALLY_AVAILABLE_STOCK}
          />
        ),
        dataIndex: 'physicallyAvailableStock',
        key: 'physicallyAvailableStock',
        align: 'left',
        ellipsis: true,
        className: 'max-width-column',
        width: 260,
        render: (_, record) =>
          record?.libraryInventory?.physicallyAvailableStock ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="BLOCKED UNITS"
            info={STOCK_COLUMN_TOOLTIPS.BLOCKED_UNITS}
          />
        ),
        dataIndex: 'pendingDispatchCount',
        key: 'pendingDispatchCount',
        align: 'left',
        ellipsis: true,
        className: 'max-width-column',
        width: 260,
        render: (_, record) =>
          record?.libraryInventory?.pendingDispatchCount ?? '-',
      },
      {
        title: 'PACKAGING MATERIAL',
        dataIndex: 'packagingMaterial',
        key: 'packagingMaterial',
        ellipsis: true,
        width: 250,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => (
          <>
            {record?.toy?.bag ? (
              <div>
                <span className="mr-8">
                  {record?.toy?.bag?.name} ({record?.toy?.bag?.size})
                </span>
                <Image
                  preview={{ visible: false }}
                  width={22}
                  src={record?.toy?.bag?.image}
                  onClick={(e) => {
                    e?.stopPropagation();
                    setPreviewVisible(true);
                    setToyPreviewImage(record?.toy?.bag?.image);
                  }}
                  className="pointer"
                />
              </div>
            ) : (
              '-'
            )}
          </>
        ),
      },
      {
        title: 'AGE GROUPS',
        dataIndex: 'ageGroups',
        key: 'ageGroups',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) =>
          record?.toy?.ageGroups?.map((ageGroup) =>
            ageGroup?.minAge > 18 ? (
              <Tag key={ageGroup?.id}>
                {Math.floor(ageGroup?.minAge / 12)} -{' '}
                {Math.floor(ageGroup?.maxAge / 12)} Years
              </Tag>
            ) : (
              <Tag key={ageGroup?.id}>
                {ageGroup?.minAge} - {ageGroup?.maxAge} Months
              </Tag>
            ),
          ),
      },
      {
        title: 'FULFILLMENT STATUS',
        dataIndex: 'fulfillmentStatus',
        key: 'fulfillmentStatus',
        ellipsis: true,
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) =>
          record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
          record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
          record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ? (
            <Badge
              color={FULFILLMENT_STATUS?.[record?.status]?.color}
              text={FULFILLMENT_STATUS?.[record?.status]?.text}
            />
          ) : (
            '-'
          ),
      },
      {
        title: 'EXPECTED RETURN',
        dataIndex: 'returnDate',
        key: 'returnDate',
        ellipsis: true,
        align: 'left',
        width: 200,
        className: 'max-width-column',
        render: (_, record) =>
          formatDate(record?.returnDate, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'NOTE',
        dataIndex: 'note',
        key: 'note',
        ellipsis: true,
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => (
          <div className="action-button">
            {record?.cancellationData?.type ===
            CANCELLATION_TYPE?.LIBRARY?.value ? (
              <Tooltip title="View comment" placement="top" zIndex={4}>
                <CommentIcon
                  className="mr-24 pointer svg-icon"
                  onClick={() => {
                    setIsNotePrompts(true);
                    setOrderItem(record);
                    setIsOrderAction(false);
                  }}
                />
              </Tooltip>
            ) : (
              <span className="mr-24">-</span>
            )}
          </div>
        ),
      },
      {
        title: 'FULFILMENT POSSIBILITY',
        dataIndex: 'fulfilmentPossibility',
        key: 'fulfilmentPossibility',
        align: 'left',
        width: 200,
        hidden: isAssignDelPartnerTab,
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPossibilityItem(record);
              setPossibilityDrawerVisible(true);
            }}
          >
            View
          </Button>
        ),
      },
      ...getStaffColumns(
        () => data?.library?.id,
        !isUnitsFeatureEnabledForLibrary(data?.library?.id),
      ),
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        width: 250,
        render: (_, record) => (
          <>
            <SelectComponent
              size="small"
              className="full-width"
              placeholder="Select Action"
              showSearch={false}
              value={
                record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
                record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value
                  ? record?.status
                  : null
              }
              disabled={
                record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
                record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
                record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ||
                (record?.transferOrders?.length > 0 &&
                  record?.transferOrders?.[0]?.status !==
                    TRANSFER_ORDER_STATUS.RECEIVED.value)
              }
              disableFulfill={DISABLE_ORDER_ITEM}
              record={record}
              options={NEW_ORDER_EXPANDED_TABLE_ACTIONS_OPTIONS}
              onChange={(value, e) => {
                handleOrderItemChange(value, record, data);
              }}
              allowClear={false}
            />
          </>
        ),
      },
    ]?.filter((item) => {
      if (isAssignDelPartnerTab && item?.dataIndex === 'actions') {
        return item?.hidden;
      }
      return !item?.hidden;
    });

    return (
      <CommonTable
        loading={isLoading[data?.id]}
        columns={nestedColumns}
        dataSource={orderItemList || []}
        pagination={false}
        rowKey={(record) => record?.id}
        className="nested-table colored-nested-table"
        rowSelection={
          isAssignDelPartnerTab ||
          !isUnitsFeatureEnabledForLibrary(data?.library?.id)
            ? undefined
            : getNestedRowSelection(data, orderItemList)
        }
      />
    );
  };

  const expandIcon = ({ expanded, onExpand, record }) =>
    expanded ? (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded disabled"
        aria-label="Expand row"
        aria-expanded="false"
        onClick={(e) => onExpand(record, e)}
        disabled={isDisabled}
      />
    ) : (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
        aria-label="Collapse row"
        aria-expanded="true"
        onClick={(e) => onExpand(record, e)}
        disabled={isDisabled}
      />
    );

  const handleCancel = () => {
    setIsContentMasterModalOpen(false);
    setOrderItem({});
  };

  const locale = {
    emptyText: isEmptyOrderList ? '' : <span />,
  };

  const selectedOrdersUniqueTags = useMemo(() => {
    const selectedOrders = (selectedRowKeys || [])
      .map((id) => selectedOrdersById?.[id])
      .filter(Boolean);

    return Array.from(
      new Map(
        selectedOrders
          ?.flatMap((o) => o?.orderTags || [])
          ?.filter((t) => t?.id)
          ?.map((t) => [t?.id, t]),
      ).values(),
    );
  }, [selectedOrdersById, selectedRowKeys]);

  const onSelectChange = (newSelectedRowKeys, selectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);

    setSelectedOrdersById((prev) => {
      const next = { ...prev };
      const currentPageOrderIds = (orderList || [])?.map((o) => o?.id);

      // Remove unchecked orders from currently visible page.
      currentPageOrderIds?.forEach((id) => {
        if (id && !newSelectedRowKeys?.includes(id)) {
          delete next[id];
        }
      });

      // Cache selected order details from currently visible rows.
      (selectedRows || [])?.forEach((selectedOrder) => {
        if (selectedOrder?.id) {
          next[selectedOrder?.id] = selectedOrder;
        }
      });

      return next;
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
    preserveSelectedRowKeys: true,
  };

  const staffLibraryIds = uniq(
    (staffSelectedOrders || [])
      ?.map((orderData) => orderData?.library?.id)
      ?.filter(Boolean),
  );
  const canBulkAssignStaff =
    hasItemSelection &&
    (staffSelectedOrders || [])?.every((orderData) =>
      isUnitsFeatureEnabledForLibrary(orderData?.library?.id),
    );

  const handleBulkStaffAssigned = (orderItemIds, field, user) => {
    setNestedData((prev) =>
      patchNestedOrderItemStaff(
        prev,
        'orderItemsAdmin',
        orderItemIds,
        field,
        user,
      ),
    );
    clearItemSelection();
  };

  const handleFilter = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = {
      ...paginationPropRef?.current,
      current: 1,
      skip: 0,
    };

    setIsOrderLoading(true);
    setIsAllFilterModalOpen(false);
    setOrderList([]);
    setIsActive(true);
    const filters = {
      status: values?.status,
      createdAt: values?.createdAt?.[1]
        ? { from: values?.createdAt?.[0], to: values?.createdAt?.[1] }
        : null,
      deliveredOn: values?.deliveredOn?.[1]
        ? { from: values?.deliveredOn?.[0], to: values?.deliveredOn?.[1] }
        : null,
      pincodes: values?.pincodes,
      name: values?.name,
      deliveryType: values?.deliveryType,
      orderTypes: values?.orderTypes,
      city: values?.city,
    };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);
    finalWhereFiltersRef.current = filters;
    saveRetainedFilterState(
      isAssignDelPartnerTab
        ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
        : TABLE_FILTER_KEYS.NEW_ORDERS,
      {
        filters,
        paginationProp: { ...paginationProp, skip: 0, current: 1 },
      },
    );
    newOrdersAdminV2({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          search: orderSearchTerm,
          deliveryType: values?.deliveryType,
          orderTypes:
            values?.orderTypes?.length > 0 ? values?.orderTypes : undefined,
          status: values?.status?.length > 0 ? values?.status : statusFilter,
          dateRange: {
            from: values?.createdAt?.[0],
            to: values?.createdAt?.[1],
          },
          deliveredOn: {
            from: values?.deliveredOn?.[0],
            to: values?.deliveredOn?.[1],
          },
          pincodeIds: values?.pincodes,
          libraryIds: values?.name,
          city: values?.city,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const handleSelectAllMobile = (checked) => {
    if (checked) {
      setSelectedRowKeys((prev) => {
        const newIds = (orderList || [])?.map((data) => data?.id);
        const mergedIds = Array.from(new Set([...prev, ...newIds]));

        setSelectedOrdersById((prevMap) => {
          const nextMap = { ...prevMap };
          (orderList || [])?.forEach((orderData) => {
            if (orderData?.id) {
              nextMap[orderData.id] = orderData;
            }
          });
          return nextMap;
        });

        return mergedIds;
      });
    } else {
      setSelectedRowKeys([]);
      setSelectedOrdersById({});
    }
  };

  const handleSelectOrders = (key) => {
    setSelectedRowKeys((prev) => {
      const selectedOrder = (orderList || [])?.find((item) => item?.id === key);
      if (prev?.includes(key)) {
        setSelectedOrdersById((prevMap) => {
          const nextMap = { ...prevMap };
          delete nextMap[key];
          return nextMap;
        });
        return prev?.filter((item) => item !== key);
      }

      if (selectedOrder?.id) {
        setSelectedOrdersById((prevMap) => ({
          ...prevMap,
          [selectedOrder.id]: selectedOrder,
        }));
      }

      return [...prev, key];
    });
  };

  const findSelectableOrders = () => orderList || [];
  return (
    <>
      <ContentMasterModal
        isModalOpen={isContentMasterModalOpen}
        contentHistory={orderItem?.contentHistory}
        handleCancel={handleCancel}
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isDeliveryPartnerPrompts}
        onOk={() =>
          handleAssignDeliveryPartner(
            [order?.id],
            existingDelPartnerRef.current
              ? existingDeliveryPartner?.value
              : deliveryPartnerData?.value,
          )
        }
        onCancel={() => setIsDeliveryPartnerPrompts(false)}
        okButtonProps={{ loading: isDeliveryPartnerLoading }}
      >
        Are you sure you want to assign
        <strong> {deliveryPartnerData?.children} </strong>?
        {existingDeliveryPartner?.value &&
          existingDeliveryPartner?.value !== deliveryPartnerData?.value && (
            <>
              <br /> OR <br />
              <Checkbox
                onChange={(e) => {
                  existingDelPartnerRef.current = e.target.checked;
                }}
              >
                {' '}
                Assign <strong>
                  {' '}
                  {existingDeliveryPartner?.children}{' '}
                </strong>{' '}
                who is already handling another order of this user.
              </Checkbox>
            </>
          )}
      </Modal>
      <DelayReasonModal
        note={
          !isEmpty(order?.metadata?.delayReason)
            ? order?.metadata
            : tempComment?.current?.[order?.id]
        }
        isModalOpen={isReasonPrompt}
        setIsModalOpen={setIsReasonPrompt}
        isOrderAction={isOrderAction}
        adminNote={
          isOrderAction ? order?.comment?.comment : orderItem?.comment?.comment
        }
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isFulfillPrompts}
        onOk={() => {
          if (
            order?.delayedInConfirmed &&
            isEmpty(order?.metadata?.delayReason)
          ) {
            setIsFulfillPrompts(false);
            setIsDelayReasonPrompts(true);
          } else {
            setIsFulfillPrompts(false);
            handleFulfillOrderItem();
          }
        }}
        onCancel={() => setIsFulfillPrompts(false)}
        okButtonProps={{ loading: isFulfillLoading }}
      >
        Are you sure you want to
        <strong> Mark as fulfill </strong>
        this order item?
      </Modal>
      <UnitScanModal
        open={isUnitScanOpen}
        mode={UNIT_SCAN_MODES?.PICK}
        orderItemId={orderItem?.id}
        toyName={orderItem?.toy?.name}
        onClose={() => setIsUnitScanOpen(false)}
        onSuccess={(data) => {
          if (data?.isOrderItemFulfilled) {
            fetchOrders();
          }
        }}
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isReadyPrompts}
        onOk={() => {
          if (
            order?.delayedInConfirmed &&
            isEmpty(order?.metadata?.delayReason)
          ) {
            setIsReadyPrompts(false);
            setIsDelayReasonPrompts(true);
          } else {
            handleReadyOrder([order?.id]);
            setIsReadyPrompts(false);
          }
        }}
        onCancel={() => setIsReadyPrompts(false)}
        okButtonProps={{ loading: isReadyLoading }}
      >
        Are you sure you want to
        <strong> Mark as ready </strong>
        this order?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isConfirmPrompts}
        onOk={() => handleReadyOrder(selectedRowKeys)}
        onCancel={() => setIsConfirmPrompts(false)}
        okButtonProps={{ loading: isReadyLoading }}
      >
        Are you sure you want to
        <strong> Confirm </strong>
        this orders?
      </Modal>
      {isDelayReasonPrompts && (
        <AddDelayReasonModal
          form={delayForm}
          isOrderAction={isOrderAction}
          isModalOpen={isDelayReasonPrompts}
          setIsModalOpen={setIsDelayReasonPrompts}
          isBtnLoading={isReadyLoading}
          onFinish={handleAddDelayReason}
        />
      )}
      <AddCommentModal
        form={form}
        isOrderAction={isOrderAction}
        isModalOpen={isAddCommentModalOpen}
        setIsModalOpen={setIsAddCommentModalOpen}
        isBtnLoading={isAddCommentLoading}
        onFinish={
          isOrderAction ? handleAddCommentInOrder : handleAddCommentInOrderItem
        }
      />
      <CommonPreviewTable
        visible={visible}
        setVisible={setVisible}
        images={toyImages}
        setImages={setToyImages}
      />
      <CommonPreview
        visible={previewVisible}
        setVisible={setPreviewVisible}
        image={toyPreviewImage}
        setImage={setToyPreviewImage}
      />
      <NoteModal
        note={
          isOrderAction ? order?.cancellationData : orderItem?.cancellationData
        }
        isModalOpen={isNotePrompts}
        setIsModalOpen={setIsNotePrompts}
        isOrderAction={isOrderAction}
        adminNote={
          isOrderAction ? order?.comment?.comment : orderItem?.comment?.comment
        }
      />
      <AssignDeliveryPartnerModal
        form={assignDelPartnerForm}
        onFinish={(values) =>
          handleAssignDeliveryPartner(selectedRowKeys, values?.name)
        }
        loadings={isDeliveryPartnerLoading}
        isModalOpen={isDeliveryPartnerModalOpen}
        setIsModalOpen={setDeliveryPartnerModalOpen}
        title="Assign Delivery Partner"
      />
      <FulfilmentPossibilityDrawer
        visible={possibilityDrawerVisible}
        onClose={() => setPossibilityDrawerVisible(false)}
        libraryInventoryId={selectedPossibilityItem?.libraryInventory?.id}
        physicallyAvailableStock={
          selectedPossibilityItem?.libraryInventory?.physicallyAvailableStock
        }
        isDesktop={isDesktop}
      />
      {!isDesktop && (
        <>
          <ShowPreOrderSwitch
            finalWhereFiltersRef={finalWhereFiltersRef}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            setPaginationProp={setPaginationProp}
            paginationPropRef={paginationPropRef}
            setOrderList={setOrderList}
            fetchOrders={fetchOrders}
            paginationProp={paginationProp}
            setIsOrderLoading={setIsOrderLoading}
            tableFilterKey={
              isAssignDelPartnerTab
                ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
                : TABLE_FILTER_KEYS.NEW_ORDERS
            }
            margin
            setSortedInfo={setSortedInfo}
            sortedInfoRef={sortedInfoRef}
            sortedInfo={sortedInfo}
          />
          <ShowDelayedOrdersSwitch
            finalWhereFiltersRef={finalWhereFiltersRef}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            setPaginationProp={setPaginationProp}
            paginationPropRef={paginationPropRef}
            setOrderList={setOrderList}
            fetchOrders={fetchOrders}
            paginationProp={paginationProp}
            setIsOrderLoading={setIsOrderLoading}
            tableFilterKey={
              isAssignDelPartnerTab
                ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
                : TABLE_FILTER_KEYS.NEW_ORDERS
            }
            margin
            setSortedInfo={setSortedInfo}
            sortedInfoRef={sortedInfoRef}
            sortedInfo={sortedInfo}
          />
        </>
      )}
      <div className="d-flex justify-end align-center mb-16">
        {isDesktop && (
          <>
            <ShowPreOrderSwitch
              finalWhereFiltersRef={finalWhereFiltersRef}
              finalWhereFilters={finalWhereFilters}
              setFinalWhereFilters={setFinalWhereFilters}
              setPaginationProp={setPaginationProp}
              paginationPropRef={paginationPropRef}
              setOrderList={setOrderList}
              fetchOrders={fetchOrders}
              paginationProp={paginationProp}
              setIsOrderLoading={setIsOrderLoading}
              tableFilterKey={
                isAssignDelPartnerTab
                  ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
                  : TABLE_FILTER_KEYS.NEW_ORDERS
              }
              setSortedInfo={setSortedInfo}
              sortedInfoRef={sortedInfoRef}
              sortedInfo={sortedInfo}
            />
            <ShowDelayedOrdersSwitch
              finalWhereFiltersRef={finalWhereFiltersRef}
              finalWhereFilters={finalWhereFilters}
              setFinalWhereFilters={setFinalWhereFilters}
              setPaginationProp={setPaginationProp}
              paginationPropRef={paginationPropRef}
              setOrderList={setOrderList}
              fetchOrders={fetchOrders}
              paginationProp={paginationProp}
              setIsOrderLoading={setIsOrderLoading}
              tableFilterKey={
                isAssignDelPartnerTab
                  ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
                  : TABLE_FILTER_KEYS.NEW_ORDERS
              }
              setSortedInfo={setSortedInfo}
              sortedInfoRef={sortedInfoRef}
              sortedInfo={sortedInfo}
            />
          </>
        )}
        <OrderStatusSelect
          finalWhereFiltersRef={finalWhereFiltersRef}
          finalWhereFilters={finalWhereFilters}
          setFinalWhereFilters={setFinalWhereFilters}
          fetchOrders={() => {
            setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
            paginationPropRef.current = {
              ...paginationPropRef?.current,
              current: 1,
              skip: 0,
            };
            setOrderList([]);
            fetchOrders();
          }}
          setIsOrderLoading={setIsOrderLoading}
          tableFilterKey={
            isAssignDelPartnerTab
              ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
              : TABLE_FILTER_KEYS.NEW_ORDERS
          }
        />
        <BulkActionButton
          selectedItemIds={selectedRowKeys}
          actions={ORDER_TAG_BULK_ACTIONS}
          scope="SYSTEM_USERS_END_USERS"
          supportsNestedSubmit
          selectedTagsForRemove={selectedOrdersUniqueTags}
          drawerTitle="Order Bulk Actions"
          className="mr-8"
          onSuccess={() => {
            setOrderList([]);
            fetchOrders();
          }}
          onClearSelection={() => {
            setSelectedRowKeys([]);
            setSelectedOrdersById({});
          }}
          tagConfigSubtype="ORDERS"
          useOrderTagsApi
          orderTagTargetField="orders"
        />
        {!isAssignDelPartnerTab && canBulkAssignStaff && (
          <BulkAssignStaffButton
            hasSelection={hasItemSelection}
            libraryIds={staffLibraryIds}
            getOrderItems={getSelectedOrderItems}
            onSuccess={handleBulkStaffAssigned}
          />
        )}
        {isAssignDelPartnerTab ? (
          <Button
            type="primary"
            disabled={!hasSelected || hasAdloggsLibrarySelected}
            className="mr-8"
            onClick={() => setDeliveryPartnerModalOpen(true)}
          >
            Assign Rider
          </Button>
        ) : (
          // commenting for now, can be used later
          <></>
          // <Button
          //   type="primary"
          //   disabled={!hasSelected}
          //   className="mr-8"
          //   onClick={() => setIsConfirmPrompts(true)}
          // >
          //   Confirm Orders
          // </Button>
        )}
        {!isDesktop && (
          <Badge dot={hasNonEmptyValueObj(finalWhereFilters)} size="small">
            <FilterIcon
              className="mobile-svg-icon ml-16"
              onClick={() => setIsAllFilterModalOpen(true)}
            />
          </Badge>
        )}
        <OrderTagsFilter
          value={selectedTagIds}
          onChange={handleTagFilterChange}
          placeholder="Filter by Tags"
          className="ml-8"
        />
        {isDesktop && (
          <SearchComponent
            className="search-box ml-16"
            query={orderSearchTerm}
            setQuery={setOrderSearchTerm}
            tableFilterKey={
              isAssignDelPartnerTab
                ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
                : TABLE_FILTER_KEYS.NEW_ORDERS
            }
            orderSearchTermRef={orderSearchTermRef}
            getData={handleSearch}
          />
        )}
      </div>

      {!isDesktop && (
        <div className="d-flex justify-end align-center">
          <SearchComponent
            className="search-box"
            query={orderSearchTerm}
            setQuery={setOrderSearchTerm}
            orderSearchTermRef={orderSearchTermRef}
            getData={handleSearch}
          />
          <Checkbox
            indeterminate={
              selectedRowKeys?.length < findSelectableOrders()?.length &&
              selectedRowKeys?.length > 0
            }
            checked={
              selectedRowKeys?.length > 0 &&
              selectedRowKeys?.length === findSelectableOrders()?.length &&
              !isOrderLoading &&
              !isFetchMoreOrderLoading
            }
            onChange={(e) => {
              handleSelectAllMobile(e?.target?.checked);
            }}
            className="ml-16"
          >
            Select all
          </Checkbox>

          <div className="ml-16 d-flex align-center">
            <SortDropdown
              isOpen={isAllSortPopoverOpen}
              setIsOpen={setIsAllSortPopoverOpen}
              options={map(sort, (item) => item)}
              sortedInfo={sortedInfo}
              setSortedInfo={setSortedInfo}
              sortedInfoRef={sortedInfoRef}
              onFinish={handleSort}
              tableFilterKey={
                isAssignDelPartnerTab
                  ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
                  : TABLE_FILTER_KEYS.NEW_ORDERS
              }
            />
          </div>
        </div>
      )}
      {isDesktop ? (
        <div className="colored-table">
          <CommonTable
            rowClassName={(record) =>
              getRowClassName(
                record,
                thresholdData,
                playNotificationSound,
                criticalOrdersRef,
              )
            }
            locale={locale}
            columns={columns}
            data={orderList || []}
            loading={isOrderLoading}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            paginationConfig={paginationProp}
            rowKey={(record) => record?.id}
            expandedRowKeys={expandedRowKeys}
            onExpand={(_, record) => setOrder(record)}
            onRow={() => ({
              style: { cursor: 'pointer' },
            })}
            expandable={{
              onExpand: (expanded, record) => handleExpand(expanded, record),
              expandedRowRender: (record) => expandedRowRender(record),
              expandIcon,
              expandRowByClick: true,
            }}
          />
        </div>
      ) : (
        <div className="mobile-container-search-action no-scroll-bar">
          <AllFilterOrderModal
            onFinish={handleFilter}
            isModalOpen={isAllFilterModalOpen}
            setIsModalOpen={setIsAllFilterModalOpen}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            isNewPath={!isAssignDelPartnerTab}
            hideStatus={!isAssignDelPartnerTab}
            isAssignDelPartnerTab={isAssignDelPartnerTab}
            finalWhereFiltersRef={finalWhereFiltersRef}
            isAdmin
            tableFilterKey={
              isAssignDelPartnerTab
                ? TABLE_FILTER_KEYS.NEW_RIDER_MANAGEMENT
                : TABLE_FILTER_KEYS.NEW_ORDERS
            }
          />
          <AllOrderItemModal
            order={order}
            isNewPath={!isAssignDelPartnerTab}
            setOrder={setOrder}
            isModalOpen={isAllOrderItemModalOpen}
            setIsModalOpen={setIsAllOrderItemModalOpen}
            handleExpand={handleExpand}
            nestedData={nestedData}
            isLoading={isLoading}
            setIsNotePrompts={setIsNotePrompts}
            setOrderItem={setOrderItem}
            setIsOrderAction={setIsOrderAction}
            handleOrderItemChange={handleOrderItemChange}
            setIsContentMasterModalOpen={setIsContentMasterModalOpen}
            setVisible={setVisible}
            setToyImages={setToyImages}
            isAdmin
            isAssignDelPartnerTab={isAssignDelPartnerTab}
          />
          <AllOrderList
            orderList={orderList}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreOrderLoading={isFetchMoreOrderLoading}
            isEmptyOrderList={isEmptyOrderList}
            isOrderLoading={isOrderLoading}
            setOrder={setOrder}
            isNewPath={!isAssignDelPartnerTab}
            setIsNotePrompts={setIsNotePrompts}
            setIsOrderAction={setIsOrderAction}
            handleOrderChange={handleOrderChange}
            setIsAllOrderItemModalOpen={setIsAllOrderItemModalOpen}
            setVisible={setVisible}
            setToyImages={setToyImages}
            handleSelectOrders={handleSelectOrders}
            selectedOrders={selectedRowKeys}
            setIsReasonPrompt={setIsReasonPrompt}
            isAdmin
            isAssignDelPartnerTab={isAssignDelPartnerTab}
            setDeliveryPartnerData={setDeliveryPartnerData}
            setIsDeliveryPartnerPrompts={setIsDeliveryPartnerPrompts}
            showBlink
            renderBlinkingDotEl={renderBlinkingDotEl}
            checkDeliveryPartner={checkDeliveryPartner}
          />
        </div>
      )}
    </>
  );
};

export default NewOrderTable;
